import { CurrencyPipe, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ApiResult } from '../../../../../../shared/models/api-result.model';
import { DropdownOption } from '../../../../../../shared/models/dropdowm.model';
import { Order } from '../../../../../models/order.model';
import { ReasonType } from '../../../../../models/reason.model';
import { OrderService } from '../../../../../services/order.service';
import { ReasonService } from '../../../../../services/reason.service';
import {
  getDefaultPaymentCard,
  getDownPayment,
  isDownPaymentRefundable,
} from '../../utils/order-details.util';

export const CANCELLATION_DETAILS_MAX_LENGTH = 500;

@Component({
  selector: 'app-order-cancel-dialog',
  imports: [
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    SelectModule,
    TextareaModule,
    CurrencyPipe,
    TitleCasePipe,
  ],
  providers: [CurrencyPipe],
  templateUrl: './order-cancel-dialog.component.html',
  styleUrl: './order-cancel-dialog.component.scss',
})
export class OrderCancelDialogComponent {
  visible = model(false);
  order = input<Order | null>(null);
  cancelled = output<void>();

  private readonly fb = inject(FormBuilder);
  private readonly orderService = inject(OrderService);
  private readonly reasonService = inject(ReasonService);
  private readonly messageService = inject(MessageService);
  private readonly currencyPipe = inject(CurrencyPipe);

  protected readonly detailsMaxLength = CANCELLATION_DETAILS_MAX_LENGTH;

  protected readonly reasons = signal<DropdownOption[]>([]);
  protected readonly reasonsLoading = signal(false);
  protected readonly reasonsError = signal(false);
  protected readonly invalidReason = signal(false);
  protected readonly submitting = signal(false);

  protected readonly form = this.fb.group({
    reasonId: this.fb.control<string | null>(null, Validators.required),
    additionalDetails: this.fb.control<string>('', [
      Validators.maxLength(CANCELLATION_DETAILS_MAX_LENGTH),
    ]),
  });

  protected readonly downPaymentRefundable = computed(() =>
    isDownPaymentRefundable(this.order()),
  );

  protected readonly downPaymentAmount = computed(
    () => getDownPayment(this.order())?.amount ?? 0,
  );

  protected readonly card = computed(() => getDefaultPaymentCard(this.order()));

  protected get detailsLength(): number {
    return this.form.controls.additionalDetails.value?.length ?? 0;
  }

  protected onShow(): void {
    this.loadReasons();
  }

  protected onVisibleChange(visible: boolean): void {
    if (this.submitting()) return;

    this.visible.set(visible);
    if (!visible) this.reset();
  }

  protected close(): void {
    this.onVisibleChange(false);
  }

  protected loadReasons(): void {
    this.reasonsLoading.set(true);
    this.reasonsError.set(false);

    this.reasonService
      .getDropdown(ReasonType.OrderCancellationByBackoffice)
      .subscribe({
        next: (response: ApiResult) => {
          this.reasonsLoading.set(false);
          this.reasons.set((response.data ?? []) as DropdownOption[]);
        },
        error: () => {
          this.reasonsLoading.set(false);
          this.reasonsError.set(true);
        },
      });
  }

  protected onReasonChange(): void {
    this.invalidReason.set(false);
  }

  protected submit(): void {
    const order = this.order();
    if (!order || this.form.invalid || this.submitting()) return;

    const { reasonId, additionalDetails } = this.form.getRawValue();

    this.submitting.set(true);
    this.invalidReason.set(false);

    this.orderService
      .cancel(order.id, {
        reasonId: reasonId!,
        additionalDetails: additionalDetails?.trim() || null,
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: this.successMessage(),
            life: 3000,
          });
          this.closeAndNotify();
        },
        error: (e) => {
          this.submitting.set(false);
          this.handleError(e);
        },
      });
  }

  private handleError(e: unknown): void {
    const status = e instanceof HttpErrorResponse ? e.status : 0;

    if (status === 422) {
      this.invalidReason.set(true);
      this.form.controls.reasonId.reset(null);
      this.loadReasons();
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail:
        status === 404
          ? 'Pedido não encontrado.'
          : 'Não foi possível cancelar o pedido. Tente novamente.',
      life: 3000,
    });

    if (status === 404) this.closeAndNotify();
  }

  private successMessage(): string {
    if (!this.downPaymentRefundable()) return 'Pedido cancelado.';

    const amount = this.currencyPipe.transform(this.downPaymentAmount());
    return `Pedido cancelado. O estorno de ${amount} foi solicitado.`;
  }

  private closeAndNotify(): void {
    this.visible.set(false);
    this.reset();
    this.cancelled.emit();
  }

  private reset(): void {
    this.form.reset({ reasonId: null, additionalDetails: '' });
    this.invalidReason.set(false);
    this.reasonsError.set(false);
  }
}
